import { DataList, ErrorState, Page, Panel, Skeleton, StatusBadge } from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import { useMyEntitlement } from "../lib/queries";

interface QuotaRow {
  readonly id: string;
  readonly quota: string;
  readonly value: number;
}

interface SourceRow {
  readonly id: string;
  readonly kind: string;
  readonly plan_id: string;
}

/** The resolved entitlement: what it grants, what it meters, and where it came from. */
export function Grants() {
  const entitlement = useMyEntitlement();

  if (entitlement.isPending) {
    return (
      <Page title="Grants" eyebrow="Platform">
        <Skeleton height={220} />
      </Page>
    );
  }

  if (entitlement.isError) {
    return (
      <Page title="Grants" eyebrow="Platform">
        <ErrorState
          headline="Entitlement unavailable"
          onRetry={() => void entitlement.refetch()}
          retryLabel="Retry"
        />
      </Page>
    );
  }

  const held = entitlement.data;
  const quotas: QuotaRow[] = Object.entries(held.quotas ?? {}).map(([quota, value]) => ({
    id: quota,
    quota,
    value,
  }));
  const sources: SourceRow[] = (held.sources ?? []).map((one, at) => ({
    id: `${one.kind}:${one.plan_id}:${String(at)}`,
    kind: one.kind,
    plan_id: one.plan_id,
  }));

  return (
    <Page title="Grants" eyebrow="Platform">
      <Panel
        title="Resolved"
        action={
          <StatusBadge tone={held.plan_id == null ? "neutral" : "info"}>
            {held.plan_id ?? "no plan"}
          </StatusBadge>
        }
      >
        <DataList
          items={[
            {
              id: held.account_id,
              title: held.account_id,
              fields: [
                {
                  label: "Capabilities",
                  value: (held.capabilities ?? []).join(", ") || "none",
                },
                {
                  label: "Activities",
                  value: (held.activities ?? []).join(", ") || "none",
                  mono: true,
                },
              ],
            },
          ]}
        />
      </Panel>

      <Panel title="Quotas">
        <PagedTable
          rows={quotas}
          pageSize={12}
          caption="Quotas"
          density="comfortable"
          emptyHeadline="Entitled to nothing"
          placeholder="Find a quota"
          match={(row, needle) => row.quota.toLowerCase().includes(needle)}
          columns={[
            { key: "quota", header: "Quota", emphasis: true, mono: true },
            { key: "value", header: "Value", numeric: true },
          ]}
        />
      </Panel>

      <Panel title="Sources">
        <PagedTable
          rows={sources}
          caption="Entitlement sources"
          emptyHeadline="No current source"
          placeholder="Find a source"
          match={(row, needle) => `${row.kind} ${row.plan_id}`.toLowerCase().includes(needle)}
          columns={[
            { key: "plan_id", header: "Plan", emphasis: true, mono: true },
            {
              key: "kind",
              header: "Kind",
              render: (row: SourceRow) => (
                <StatusBadge tone={row.kind === "grant" ? "info" : "success"}>
                  {row.kind}
                </StatusBadge>
              ),
            },
          ]}
        />
      </Panel>
    </Page>
  );
}
